import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { auth, db, appId } from './firebase-config';
import { signInAnonymously } from 'firebase/auth';
import {
  collection,
  collectionGroup,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
  getDocs
} from 'firebase/firestore';
import { resolveTransportsCollectionPath, resolveRoutesCollectionPath, resolveRouteDocPath } from './core/company/companyPaths';
import RouteDetail from './modules/logistics/components/RouteDetail';
import { Icon, calculateDistance, getLocalDateAsString, getRouteStatus } from './modules/logistics/components/LogisticsUtils';
import { mixHexColor, resolveTransportIdentity, withAlpha } from './modules/logistics/components/transportIdentity';
import {
  buildDriverDisplayName,
  buildOperationalAccessPath,
  buildTransportDisplayName,
  buildTransportMeta
} from './modules/logistics/components/driverUtils';
import 'leaflet/dist/leaflet.css';

// --- Constantes ---
const DEFAULT_COMPANY_ID = 'default';
const DEFAULT_COMPANY_NAME = 'Empresa Base';
const ACTIVE_ROUTE_STATUSES = ['in_progress', 'pending', 'programmed'];
const STRICT_TRANSPORT_MATCH_ROUTE_STATUSES = ['in_progress', 'pending', 'programmed'];

// --- Helpers ---
const GEO_MIN_UPDATE_INTERVAL_MS = 15000;
const GEO_MIN_DISTANCE_METERS = 25;
const GEO_MAX_PATH_POINTS = 120;
const HEADER_AUTO_COLLAPSE_MS = 2000;

const getTimestampMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasValidLatLng = (point) =>
  Number.isFinite(Number(point?.lat)) && Number.isFinite(Number(point?.lng));

const formatTimeAgo = (value) => {
  const time = getTimestampMillis(value);
  if (!time) return 'Sin señal';

  const diff = Math.max(0, Date.now() - time);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 10) return 'Ahora';
  if (seconds < 60) return `Hace ${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `Hace ${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours}h ${minutes % 60}m`;

  const days = Math.floor(hours / 24);
  return `Hace ${days}d`;
};

const getTransportAvatarLabel = (transport = {}) => {
  const base = String(transport?.currentDriverName || transport?.driverName || transport?.name || transport?.vehicleLabel || 'TR')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk.charAt(0).toUpperCase())
    .join('');

  return base || 'TR';
};

const shouldAppendLocationPoint = (previousPoint, nextPoint) => {
  if (!hasValidLatLng(nextPoint)) return false;
  if (!hasValidLatLng(previousPoint)) return true;

  const distanceMeters =
    calculateDistance(
      Number(previousPoint.lat),
      Number(previousPoint.lng),
      Number(nextPoint.lat),
      Number(nextPoint.lng)
    ) * 1000;

  if (distanceMeters >= GEO_MIN_DISTANCE_METERS) return true;

  const timeDiff =
    getTimestampMillis(nextPoint.timestamp) - getTimestampMillis(previousPoint.timestamp);

  return timeDiff >= 60000;
};

const extractCompanyIdFromPath = (path = '') => {
  const parts = String(path).split('/').filter(Boolean);
  const companyIndex = parts.indexOf('companies');
  if (companyIndex >= 0 && parts[companyIndex + 1]) {
    return parts[companyIndex + 1];
  }
  return null;
};

const buildTransportAuthKeys = (transportId, companyId = DEFAULT_COMPANY_ID) => {
  if (!transportId) return [];
  return [
    `transporterAuth_${companyId}_${transportId}`,
    `transporterAuth_${transportId}`,
  ];
};

const persistTransportAccess = (transportId, companyId = DEFAULT_COMPANY_ID, sessionData = {}) => {
  buildTransportAuthKeys(transportId, companyId).forEach((key) => {
    localStorage.setItem(key, 'true');
    if (sessionData && Object.keys(sessionData).length > 0) {
      localStorage.setItem(
        `${key}_session`,
        JSON.stringify({
          ...sessionData,
          storedAt: new Date().toISOString()
        })
      );
    }
  });
};

const hasStoredTransportAccess = (transportId, companyId = DEFAULT_COMPANY_ID) => {
  return buildTransportAuthKeys(transportId, companyId).some(
    (key) => localStorage.getItem(key) === 'true'
  );
};

const clearTransportAccess = (transportId, companyId = DEFAULT_COMPANY_ID) => {
  buildTransportAuthKeys(transportId, companyId).forEach((key) => {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_session`);
  });
};

const ensureDriverFirebaseSession = async () => {
  if (!auth) {
    throw new Error('Firebase Auth no está inicializado.');
  }

  if (auth.currentUser) {
    return auth.currentUser;
  }

  const credential = await signInAnonymously(auth);
  return credential.user;
};

const getRouteStatusPriority = (route) => {
  const status = getRouteStatus(route);
  if (status === 'in_progress') return 3;
  if (status === 'pending') return 2;
  if (status === 'programmed') return 1;
  return 0;
};

const getRouteSortTime = (route) => {
  if (route?.updatedAt?.toDate) return route.updatedAt.toDate().getTime();
  if (route?.createdAt?.toDate) return route.createdAt.toDate().getTime();
  return 0;
};

const sortCandidateRoutes = (routes = []) => {
  return [...routes].sort((a, b) => {
    const priorityDiff = getRouteStatusPriority(b) - getRouteStatusPriority(a);
    if (priorityDiff !== 0) return priorityDiff;
    return getRouteSortTime(b) - getRouteSortTime(a);
  });
};

const buildRouteMergeKey = (route = {}) => {
  const logicalId = String(route?.legacySourceId || route?.id || '').trim();
  if (logicalId) return logicalId;

  return [
    String(route?.date || '').trim(),
    String(route?.transporterId || route?.transporterSlug || route?.transportType || route?.transport || '').trim(),
    String(route?.name || '').trim()
  ].join('::');
};

const mergeOperationalRoutes = (companyRoutes = [], legacyRoutes = []) => {
  const companyByKey = new Map(
    companyRoutes
      .map((route) => [buildRouteMergeKey(route), route])
      .filter(([key]) => Boolean(key))
  );

  const mergedLegacyRoutes = legacyRoutes.map((route) => {
    const duplicateCompanyRoute = companyByKey.get(buildRouteMergeKey(route));
    if (!duplicateCompanyRoute) return route;

    return {
      ...route,
      ...duplicateCompanyRoute,
      companyId: duplicateCompanyRoute.companyId || route.companyId,
      companyName: duplicateCompanyRoute.companyName || route.companyName,
      _source: 'company',
      legacySourceId:
        route.legacySourceId ||
        route.id ||
        duplicateCompanyRoute.legacySourceId ||
        duplicateCompanyRoute.id
    };
  });

  const legacyKeys = new Set(mergedLegacyRoutes.map((route) => buildRouteMergeKey(route)).filter(Boolean));
  const companyOnlyRoutes = companyRoutes.filter((route) => !legacyKeys.has(buildRouteMergeKey(route)));
  return [...mergedLegacyRoutes, ...companyOnlyRoutes];
};

const selectBestRouteForTransport = (routes = [], transport) => {
  if (!transport) return null;

  const activeRoutes = routes.filter((route) =>
    STRICT_TRANSPORT_MATCH_ROUTE_STATUSES.includes(getRouteStatus(route))
  );

  if (activeRoutes.length === 0) return null;

  const normalizedTransportId = String(transport.id || '').trim();
  const normalizedTransportSlug = String(transport.slug || '').trim();

  const byTransporterId = sortCandidateRoutes(
    activeRoutes.filter((route) =>
      normalizedTransportId && String(route.transporterId || '').trim() === normalizedTransportId
    )
  );
  if (byTransporterId.length > 0) return byTransporterId[0];

  const bySlug = sortCandidateRoutes(
    activeRoutes.filter((route) =>
      normalizedTransportSlug && String(route.transporterSlug || '').trim() === normalizedTransportSlug
    )
  );
  if (bySlug.length > 0) return bySlug[0];

  return null;
};

const normalizeTransportDoc = (docSnap, source, fallbackCompanyId = DEFAULT_COMPANY_ID, fallbackCompanyName = DEFAULT_COMPANY_NAME) => {
  const data = docSnap.data();
  const derivedCompanyId =
    source === 'company'
      ? extractCompanyIdFromPath(docSnap.ref.path) || data.companyId || fallbackCompanyId
      : data.companyId || fallbackCompanyId;

  return {
    id: docSnap.id,
    ...data,
    _source: source,
    companyId: derivedCompanyId,
    companyName: data.companyName || (derivedCompanyId === DEFAULT_COMPANY_ID ? fallbackCompanyName : derivedCompanyId),
  };
};

const findFirstTransportByAccessSlug = async (collectionRef, transporterSlug) => {
  const slugFields = ['slug', 'driverAccessSlug', 'accessSlug'];

  for (const fieldName of slugFields) {
    const candidateQuery = query(collectionRef, where(fieldName, '==', transporterSlug));
    const candidateSnapshot = await getDocs(candidateQuery);

    if (!candidateSnapshot.empty) {
      return candidateSnapshot;
    }
  }

  return null;
};

const buildRouteDocReference = (route, fallbackCompanyId = DEFAULT_COMPANY_ID) => {
  if (!route?.id) return null;

  if (route?._source === 'legacy') {
    return doc(db, `artifacts/${appId}/logisticsRoutes/${route.id}`);
  }

  return doc(
    db,
    resolveRouteDocPath(route.companyId || fallbackCompanyId, route.id)
  );
};

const findTransportBySlug = async (transporterSlug, preferredCompanyId, preferredCompanyName) => {
  // 1) primero intentamos en la empresa activa
  if (preferredCompanyId) {
    try {
      const companyTransportsRef = collection(db, resolveTransportsCollectionPath(preferredCompanyId));
      const companySnapshot = await findFirstTransportByAccessSlug(companyTransportsRef, transporterSlug);

      if (companySnapshot && !companySnapshot.empty) {
        return normalizeTransportDoc(
          companySnapshot.docs[0],
          'company',
          preferredCompanyId,
          preferredCompanyName
        );
      }
    } catch (error) {
      console.warn('No pude resolver el acceso operativo en la colección privada de la empresa. Sigo con fallback público.', error);
    }
  }

  // 2) luego buscamos en cualquier empresa multiempresa
  try {
    let groupSnapshot = null;
    const slugFields = ['slug', 'driverAccessSlug', 'accessSlug'];

    for (const fieldName of slugFields) {
      const groupQuery = query(collectionGroup(db, 'transports'), where(fieldName, '==', transporterSlug));
      const candidateSnapshot = await getDocs(groupQuery);
      if (!candidateSnapshot.empty) {
        groupSnapshot = candidateSnapshot;
        break;
      }
    }

    if (groupSnapshot && !groupSnapshot.empty) {
      const matchingDoc =
        groupSnapshot.docs.find((docSnap) => {
          const companyId = extractCompanyIdFromPath(docSnap.ref.path);
          return preferredCompanyId && companyId === preferredCompanyId;
        }) || groupSnapshot.docs[0];

      return normalizeTransportDoc(
        matchingDoc,
        'company',
        preferredCompanyId || DEFAULT_COMPANY_ID,
        preferredCompanyName || DEFAULT_COMPANY_NAME
      );
    }
  } catch (error) {
    console.warn('No pude resolver el acceso operativo por collectionGroup. Sigo con fallback público.', error);
  }

  // 3) fallback legacy
  const legacyRef = collection(db, `artifacts/${appId}/public/data/transports`);
  const legacySnapshot = await findFirstTransportByAccessSlug(legacyRef, transporterSlug);

  if (legacySnapshot && !legacySnapshot.empty) {
    return normalizeTransportDoc(
      legacySnapshot.docs[0],
      'legacy',
      preferredCompanyId || DEFAULT_COMPANY_ID,
      preferredCompanyName || DEFAULT_COMPANY_NAME
    );
  }

  return null;
};

// --- Componente de Acceso para Choferes por unidad individual ---
const TransporterAccess = ({
  transporterSlug,
  companyId = DEFAULT_COMPANY_ID,
  companyName = DEFAULT_COMPANY_NAME,
  onLoginSuccess,
  showMessage
}) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const transportData = await findTransportBySlug(transporterSlug, companyId, companyName);

      if (!transportData) {
        setErrorMessage('No se encontró el acceso operativo.');
        showMessage('No se encontró el acceso operativo.', 'error');
        setLoading(false);
        return;
      }

      if (transportData.password === password || transportData.driverPassword === password) {
        const driverAuthUser = await ensureDriverFirebaseSession();
        const enrichedTransportData = {
          ...transportData,
          driverAccessAuthUid: driverAuthUser?.uid || '',
          driverAccessProvider: driverAuthUser?.isAnonymous ? 'anonymous' : 'firebase'
        };

        persistTransportAccess(transportData.id, transportData.companyId, {
          uid: driverAuthUser?.uid || '',
          provider: driverAuthUser?.isAnonymous ? 'anonymous' : 'firebase',
          slug: transportData.driverAccessSlug || transportData.slug || transporterSlug || '',
          companyId: transportData.companyId || DEFAULT_COMPANY_ID,
          transportId: transportData.id || ''
        });

        onLoginSuccess(enrichedTransportData);
      } else {
        setErrorMessage('Contraseña incorrecta. Intenta de nuevo.');
        showMessage('Contraseña incorrecta. Intenta de nuevo.', 'error');
        setPassword('');
      }
    } catch (error) {
      console.error('Error al verificar la contraseña del acceso operativo:', error);
      setErrorMessage('Error al verificar la contraseña. Intenta de nuevo.');
      showMessage('Error al verificar la contraseña.', 'error');
      setPassword('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4 bg-gradient-to-br from-slate-200 to-slate-400">
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          background-image: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.4) 50%, rgba(255, 255, 255, 0) 100%);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }
      `}</style>

      <div className="w-full max-w-sm p-8 space-y-6 bg-white rounded-xl shadow-2xl border border-slate-200 animate-fade-in-down">
        <div className="text-center">
          <Icon
            path="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25"
            className="w-16 h-16 mx-auto text-blue-600 drop-shadow-md"
          />
          <h2 className="mt-6 text-3xl font-extrabold text-slate-800">
            Acceso de Chofer
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Ingresa la contraseña para ver tu planilla del día.
          </p>
          <p className="mt-2 text-xs text-slate-400 font-mono">
            Acceso: /transporter/{transporterSlug}
          </p>
          <p className="mt-2 text-xs text-cyan-700 font-semibold">
            Empresa activa: {companyName}
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleLogin}>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Contraseña"
              required
              className="w-full px-4 py-3 text-slate-700 bg-slate-50 border border-slate-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
              title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              <Icon
                path={
                  showPassword
                    ? "M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.774 3.162 10.066 7.476-1.092 3.821-4.104 6.49-7.533 8.22M15 12a3 3 0 11-6 0 3 3 0 016 0zm-6 0a3 3 0 006 0"
                    : "M2.036 12.328a.9.9 0 000 1.344S4.325 17.5 12 17.5c7.675 0 9.964-3.828 9.964-3.828a.9.9 0 000-1.344S19.675 8.5 12 8.5c-7.675 0-9.964 3.828-9.964 3.828zM12 12a3 3 0 100-6 3 3 0 000 6z"
                }
                className="w-5 h-5 text-slate-400 hover:text-slate-600 transition-colors"
              />
            </button>
          </div>

          {errorMessage && (
            <div className="flex items-center gap-2 p-3 text-sm font-semibold text-red-700 bg-red-100 rounded-lg">
              <Icon
                path="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                className="w-5 h-5"
              />
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-3 font-bold text-white bg-blue-600 rounded-lg shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 transition-all transform hover:scale-105"
          >
            {loading ? 'Verificando...' : 'Acceder'}
          </button>
        </form>
      </div>
    </div>
  );
};

// --- Componente Principal de Logística para Choferes por unidad ---
const TransporterLogisticsApp = ({
  showMessage,
  transporterSlug,
  companyId = DEFAULT_COMPANY_ID,
  companyName = DEFAULT_COMPANY_NAME
}) => {
  const [isAccessGranted, setIsAccessGranted] = useState(false);
  const [transport, setTransport] = useState(null);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transportLoading, setTransportLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [showHeaderHint, setShowHeaderHint] = useState(false);
  const [geoState, setGeoState] = useState({
    status: 'idle',
    permission: 'prompt',
    error: '',
    currentPosition: null,
    lastSignalAt: null,
    accuracy: null
  });
  const [geoRetryKey, setGeoRetryKey] = useState(0);
  const watchIdRef = useRef(null);
  const liveRouteRef = useRef(null);
  const persistedLocationRef = useRef({
    lastPersistedAt: 0,
    lastPoint: null,
    lastRouteId: null
  });
  const headerCollapseTimeoutRef = useRef(null);

  const titleText = useMemo(() => {
    const unitLabel = buildTransportDisplayName(transport || route || {}, '');
    return unitLabel ? `Mi Ruta Activa · ${unitLabel}` : 'Mi Ruta Activa';
  }, [transport, route]);

  const transportMetaLabel = useMemo(
    () => buildTransportMeta(transport || route || {}),
    [transport, route]
  );

  const driverHeaderLabel = useMemo(
    () => buildDriverDisplayName(transport || route || {}, 'Chofer sin asignar'),
    [transport, route]
  );

  const operationalAccessPath = useMemo(
    () => buildOperationalAccessPath(transport || route || {}),
    [transport, route]
  );

  const resolvedCompanyName = useMemo(() => {
    if (transport?.companyName) return transport.companyName;
    return companyName || DEFAULT_COMPANY_NAME;
  }, [transport?.companyName, companyName]);

  const resolvedCompanyId = useMemo(() => {
    if (transport?.companyId) return transport.companyId;
    return companyId || DEFAULT_COMPANY_ID;
  }, [transport?.companyId, companyId]);

  const effectiveTransporterLocation =
    geoState.currentPosition || route?.transporterLastLocation || null;

  const transportIdentity = useMemo(() => resolveTransportIdentity(transport || route || {}), [transport, route]);
  const transportAccent = transportIdentity.identityColor;
  const transportSoft = transportIdentity.identitySoftColor || mixHexColor(transportAccent, '#ffffff', 0.82);
  const transportPhotoURL =
    typeof transport?.currentDriverPhotoURL === 'string' && transport.currentDriverPhotoURL.trim()
      ? transport.currentDriverPhotoURL.trim()
      : typeof transport?.photoURL === 'string' && transport.photoURL.trim()
        ? transport.photoURL.trim()
      : typeof route?.photoURL === 'string' && route.photoURL.trim()
        ? route.photoURL.trim()
        : '';
  const transportAvatarLabel = useMemo(() => getTransportAvatarLabel(transport || route || {}), [transport, route]);
  const headerShellStyle = useMemo(() => ({
    backgroundImage: `
      linear-gradient(180deg, ${mixHexColor('#07111f', transportAccent, 0.18)} 0%, ${mixHexColor('#0f172a', transportAccent, 0.1)} 100%),
      radial-gradient(circle at top right, ${withAlpha(transportAccent, 0.34)} 0%, transparent 34%),
      radial-gradient(circle at left center, ${withAlpha(transportSoft, 0.22)} 0%, transparent 28%)
    `,
    borderColor: withAlpha(transportAccent, 0.34),
    boxShadow: `0 24px 80px ${withAlpha(transportAccent, 0.2)}`
  }), [transportAccent, transportSoft]);
  const headerPillStyle = useMemo(() => ({
    borderColor: withAlpha(transportAccent, 0.28),
    background: withAlpha(transportAccent, 0.14),
    color: mixHexColor(transportAccent, '#ffffff', 0.72)
  }), [transportAccent]);
  const avatarShellStyle = useMemo(() => ({
    backgroundImage: `linear-gradient(180deg, ${mixHexColor('#ffffff', transportAccent, 0.08)} 0%, ${mixHexColor('#dce4ec', transportAccent, 0.2)} 100%)`,
    boxShadow: `0 14px 32px ${withAlpha(transportAccent, 0.2)}`
  }), [transportAccent]);

  const effectiveTransporterPath = useMemo(() => {
    const persistedPath = Array.isArray(route?.transporterLivePath)
      ? route.transporterLivePath
      : [];

    if (!geoState.currentPosition) return persistedPath;
    if (!persistedPath.length) return [geoState.currentPosition];

    const lastPoint = persistedPath[persistedPath.length - 1];
    const sameTimestamp =
      getTimestampMillis(lastPoint?.timestamp) ===
      getTimestampMillis(geoState.currentPosition?.timestamp);

    if (sameTimestamp) return persistedPath;

    return [...persistedPath, geoState.currentPosition].slice(-GEO_MAX_PATH_POINTS);
  }, [route?.transporterLivePath, geoState.currentPosition]);

  const geoStatusMeta = useMemo(() => {
    switch (geoState.status) {
      case 'active':
        return {
          label: route ? 'Ubicación activa' : 'Ubicación lista',
          tone: 'bg-emerald-50 border-emerald-200 text-emerald-700',
          icon: 'M9 12.75L11.25 15 15 9.75m6 2.25a9 9 0 11-18 0 9 9 0 0118 0z'
        };
      case 'requesting':
        return {
          label: 'Activando ubicación',
          tone: 'bg-cyan-50 border-cyan-200 text-cyan-700',
          icon: 'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992'
        };
      case 'denied':
        return {
          label: 'Permiso bloqueado',
          tone: 'bg-rose-50 border-rose-200 text-rose-700',
          icon: 'M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.646c-1.73 0-2.813-1.874-1.948-3.374L10.052 3.378c.866-1.5 3.032-1.5 3.898 0l7.353 12.748zM12 15.75h.007v.008H12v-.008z'
        };
      case 'unsupported':
        return {
          label: 'GPS no disponible',
          tone: 'bg-slate-100 border-slate-200 text-slate-600',
          icon: 'M12 18.75a6.75 6.75 0 006.75-6.75m-13.5 0A6.75 6.75 0 0112 5.25m0 13.5V21m0-15.75V3m0 0a9 9 0 00-9 9m9-9a9 9 0 019 9m-9 9a9 9 0 01-9-9m9 9a9 9 0 009-9'
        };
      default:
        return {
          label: 'Ubicación pendiente',
          tone: 'bg-amber-50 border-amber-200 text-amber-700',
          icon: 'M12 6v6l4 2.25m5.25-2.25a9 9 0 11-18 0 9 9 0 0118 0z'
        };
    }
  }, [geoState.status, route]);

  useEffect(() => {
    if (!isAccessGranted) return undefined;

    setIsHeaderCollapsed(false);
    setShowHeaderHint(false);

    if (headerCollapseTimeoutRef.current) {
      clearTimeout(headerCollapseTimeoutRef.current);
    }

    headerCollapseTimeoutRef.current = setTimeout(() => {
      setIsHeaderCollapsed(true);
      setShowHeaderHint(true);
    }, HEADER_AUTO_COLLAPSE_MS);

    return () => {
      if (headerCollapseTimeoutRef.current) {
        clearTimeout(headerCollapseTimeoutRef.current);
        headerCollapseTimeoutRef.current = null;
      }
    };
  }, [isAccessGranted, route?.id, transport?.id]);

  const handleRetryGeolocation = useCallback(() => {
    setGeoRetryKey((value) => value + 1);
  }, []);

  const toggleHeaderCollapsed = useCallback(() => {
    if (headerCollapseTimeoutRef.current) {
      clearTimeout(headerCollapseTimeoutRef.current);
      headerCollapseTimeoutRef.current = null;
    }

    setShowHeaderHint(false);
    setIsHeaderCollapsed((value) => !value);
  }, []);

  // Cargamos el transporte por slug
  useEffect(() => {
    if (!transporterSlug) {
      setError('No se recibió un acceso válido para el transporte.');
      setTransportLoading(false);
      return;
    }

    const loadTransport = async () => {
      try {
        const transportData = await findTransportBySlug(transporterSlug, companyId, companyName);

        if (!transportData) {
          setError('No se encontró el transporte solicitado.');
          setTransport(null);
        } else {
          let nextTransportData = transportData;

          if (hasStoredTransportAccess(transportData.id, transportData.companyId)) {
            try {
              const driverAuthUser = await ensureDriverFirebaseSession();
              nextTransportData = {
                ...transportData,
                driverAccessAuthUid: driverAuthUser?.uid || '',
                driverAccessProvider: driverAuthUser?.isAnonymous ? 'anonymous' : 'firebase'
              };
              persistTransportAccess(transportData.id, transportData.companyId, {
                uid: driverAuthUser?.uid || '',
                provider: driverAuthUser?.isAnonymous ? 'anonymous' : 'firebase',
                slug: transportData.driverAccessSlug || transportData.slug || transporterSlug || '',
                companyId: transportData.companyId || DEFAULT_COMPANY_ID,
                transportId: transportData.id || ''
              });
              setIsAccessGranted(true);
            } catch (sessionError) {
              console.error('No pude preparar sesión Firebase del chofer:', sessionError);
              clearTransportAccess(transportData.id, transportData.companyId);
              setIsAccessGranted(false);
            }
          }

          setTransport(nextTransportData);
        }
      } catch (err) {
        console.error('Error al obtener el transporte:', err);
        setError('Error al cargar el acceso operativo.');
      } finally {
        setTransportLoading(false);
      }
    };

    loadTransport();
  }, [transporterSlug, companyId, companyName]);

  // Cargamos la ruta de hoy para esa unidad en tiempo real
  useEffect(() => {
    if (!isAccessGranted || !transport?.id) {
      if (!transportLoading) {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    const today = getLocalDateAsString(new Date());

    let companyTodayRoutes = [];
    let legacyTodayRoutes = [];
    const shouldUseLegacyRoutes = !transport.companyId || transport?._source === 'legacy';
    let companyReady = !transport.companyId;
    let legacyReady = !shouldUseLegacyRoutes;
    let isCancelled = false;

    const syncBestRoute = () => {
      if (isCancelled || !companyReady || !legacyReady) return;

      const mergedRoutes = mergeOperationalRoutes(companyTodayRoutes, legacyTodayRoutes);
      const bestRoute = selectBestRouteForTransport(mergedRoutes, transport);
      setRoute(bestRoute || null);
      setLoading(false);
      setError(null);
    };

    let unsubscribeCompany = () => {};

    if (transport.companyId) {
      const companyRoutesRef = collection(db, resolveRoutesCollectionPath(transport.companyId));
      const companyTodayQuery = query(companyRoutesRef, where('date', '==', today));

      unsubscribeCompany = onSnapshot(
        companyTodayQuery,
        (companySnapshot) => {
          companyReady = true;
          companyTodayRoutes = companySnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            companyId: docSnap.data()?.companyId || transport.companyId || resolvedCompanyId,
            companyName: docSnap.data()?.companyName || transport.companyName || resolvedCompanyName,
            _source: 'company'
          }));
          syncBestRoute();
        },
        (companyError) => {
          console.error('Error al cargar rutas de empresa para chofer:', companyError);
          companyReady = true;
          companyTodayRoutes = [];
          syncBestRoute();
        }
      );
    }

    let unsubscribeLegacy = () => {};

    if (shouldUseLegacyRoutes) {
      const legacyRoutesRef = collection(db, `artifacts/${appId}/logisticsRoutes`);
      const legacyTodayQuery = query(legacyRoutesRef, where('date', '==', today));

      unsubscribeLegacy = onSnapshot(
        legacyTodayQuery,
        (legacySnapshot) => {
          legacyReady = true;
          legacyTodayRoutes = legacySnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
            _source: 'legacy'
          }));
          syncBestRoute();
        },
        (err) => {
          console.error('Error al obtener la ruta legacy del chofer:', err);
          legacyReady = true;
          legacyTodayRoutes = [];
          setError('Error al cargar la ruta. Intenta de nuevo más tarde.');
          syncBestRoute();
        }
      );
    }

    return () => {
      isCancelled = true;
      unsubscribeCompany();
      unsubscribeLegacy();
    };
  }, [isAccessGranted, transport, transportLoading]);

  useEffect(() => {
    liveRouteRef.current = route;
  }, [route]);

  useEffect(() => {
    if (!isAccessGranted || typeof navigator === 'undefined' || !navigator.permissions?.query) return undefined;

    let isMounted = true;
    let permissionStatus = null;

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((status) => {
        if (!isMounted) return;

        permissionStatus = status;
        setGeoState((prev) => ({ ...prev, permission: status.state }));

        permissionStatus.onchange = () => {
          setGeoState((prev) => ({ ...prev, permission: permissionStatus.state }));
        };
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, [isAccessGranted]);

  useEffect(() => {
    if (!isAccessGranted || !transport?.id) {
      if (watchIdRef.current && typeof navigator !== 'undefined' && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      return undefined;
    }

    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setGeoState((prev) => ({
        ...prev,
        status: 'unsupported',
        error: 'Este dispositivo o navegador no permite geolocalización.'
      }));
      return undefined;
    }

    if (watchIdRef.current) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setGeoState((prev) => ({ ...prev, status: 'requesting', error: '' }));

    const handlePositionSuccess = async (position) => {
      const nextLocation = {
        lat: Number(position.coords.latitude),
        lng: Number(position.coords.longitude),
        timestamp: new Date(),
        accuracy: Number(position.coords.accuracy || 0)
      };

      setGeoState((prev) => ({
        ...prev,
        status: 'active',
        error: '',
        currentPosition: nextLocation,
        lastSignalAt: nextLocation.timestamp,
        accuracy: nextLocation.accuracy
      }));

      const currentRoute = liveRouteRef.current;
      const routeRef = buildRouteDocReference(currentRoute, resolvedCompanyId);

      if (!currentRoute?.id || !routeRef) return;

      const currentPersistedPath = Array.isArray(currentRoute.transporterLivePath)
        ? currentRoute.transporterLivePath
        : [];

      const previousPersistedPoint =
        persistedLocationRef.current.lastRouteId === currentRoute.id
          ? persistedLocationRef.current.lastPoint
          : currentPersistedPath[currentPersistedPath.length - 1] ||
            currentRoute.transporterLastLocation ||
            null;

      const nowMs = Date.now();
      const lastPersistedMs =
        persistedLocationRef.current.lastRouteId === currentRoute.id
          ? persistedLocationRef.current.lastPersistedAt
          : getTimestampMillis(previousPersistedPoint?.timestamp);

      const movedEnough = !hasValidLatLng(previousPersistedPoint)
        ? true
        : calculateDistance(
            Number(previousPersistedPoint.lat),
            Number(previousPersistedPoint.lng),
            nextLocation.lat,
            nextLocation.lng
          ) * 1000 >= GEO_MIN_DISTANCE_METERS;

      const enoughTimePassed = !lastPersistedMs || nowMs - lastPersistedMs >= GEO_MIN_UPDATE_INTERVAL_MS;
      const appendToPath = shouldAppendLocationPoint(previousPersistedPoint, nextLocation);

      if (!movedEnough && !enoughTimePassed) return;

      const updatePayload = {
        transporterLastLocation: nextLocation,
        updatedAt: new Date(),
        lastDriverWriteAt: new Date(),
        lastDriverWriteSource: 'transporter-view-gps',
        lastDriverTransportId: transport?.id || currentRoute?.transporterId || '',
        lastDriverTransportSlug: transport?.driverAccessSlug || transport?.slug || currentRoute?.transporterSlug || '',
        lastDriverAuthUid: auth?.currentUser?.uid || transport?.driverAccessAuthUid || ''
      };

      if (appendToPath) {
        updatePayload.transporterLivePath = [
          ...currentPersistedPath,
          nextLocation
        ].slice(-GEO_MAX_PATH_POINTS);
      }

      try {
        await updateDoc(routeRef, updatePayload);
        persistedLocationRef.current = {
          lastPersistedAt: nowMs,
          lastPoint: nextLocation,
          lastRouteId: currentRoute.id
        };
      } catch (persistError) {
        console.error('Error actualizando geolocalización del transportista:', persistError);
      }
    };

    const handlePositionError = (geoError) => {
      let message = 'No se pudo obtener la ubicación del dispositivo.';
      let status = 'error';

      if (geoError?.code === 1) {
        message = 'Permiso de ubicación denegado. Habilitalo para usar el monitor en vivo.';
        status = 'denied';
      } else if (geoError?.code === 2) {
        message = 'No se pudo determinar la ubicación actual.';
      } else if (geoError?.code === 3) {
        message = 'La ubicación tardó demasiado en responder. Reintentá en unos segundos.';
      }

      setGeoState((prev) => ({
        ...prev,
        status,
        error: message
      }));

      showMessage(message, status === 'denied' ? 'warning' : 'error');
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePositionSuccess,
      handlePositionError,
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 10000
      }
    );

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [isAccessGranted, transport?.id, resolvedCompanyId, geoRetryKey, showMessage]);

  useEffect(() => {
    if (!route?.id || !geoState.currentPosition) return;

    const alreadyPersistedForRoute =
      persistedLocationRef.current.lastRouteId === route.id &&
      getTimestampMillis(persistedLocationRef.current.lastPoint?.timestamp) ===
        getTimestampMillis(geoState.currentPosition.timestamp);

    if (alreadyPersistedForRoute) return;

    const routeRef = buildRouteDocReference(route, resolvedCompanyId);
    if (!routeRef) return;

    const persistedPath = Array.isArray(route.transporterLivePath)
      ? route.transporterLivePath
      : [];

    const lastPathPoint = persistedPath[persistedPath.length - 1] || route.transporterLastLocation || null;
    const appendToPath = shouldAppendLocationPoint(lastPathPoint, geoState.currentPosition);

    const updatePayload = {
      transporterLastLocation: geoState.currentPosition,
      updatedAt: new Date(),
      lastDriverWriteAt: new Date(),
      lastDriverWriteSource: 'transporter-view-gps-initial',
      lastDriverTransportId: transport?.id || route?.transporterId || '',
      lastDriverTransportSlug: transport?.driverAccessSlug || transport?.slug || route?.transporterSlug || '',
      lastDriverAuthUid: auth?.currentUser?.uid || transport?.driverAccessAuthUid || ''
    };

    if (appendToPath) {
      updatePayload.transporterLivePath = [
        ...persistedPath,
        geoState.currentPosition
      ].slice(-GEO_MAX_PATH_POINTS);
    }

    updateDoc(routeRef, updatePayload)
      .then(() => {
        persistedLocationRef.current = {
          lastPersistedAt: Date.now(),
          lastPoint: geoState.currentPosition,
          lastRouteId: route.id
        };
      })
      .catch((persistError) => {
        console.error('Error sincronizando GPS inicial de la ruta:', persistError);
      });
  }, [route?.id, route?.transporterLivePath, route?.transporterLastLocation, geoState.currentPosition, resolvedCompanyId, transport]);

  const handleLoginSuccess = (transportData) => {
    setTransport(transportData);
    setIsAccessGranted(true);
  };

  const handleLogout = () => {
    if (watchIdRef.current && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    if (transport?.id) {
      clearTransportAccess(transport.id, transport.companyId);
    }

    setGeoState({
      status: 'idle',
      permission: 'prompt',
      error: '',
      currentPosition: null,
      lastSignalAt: null,
      accuracy: null
    });
    setIsAccessGranted(false);
    setRoute(null);
    setError(null);
  };

  if (transportLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Icon
          path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
          className="w-12 h-12 text-blue-500 animate-spin"
        />
      </div>
    );
  }

  if (error && !transport) {
    return <div className="text-center p-8 text-red-500">{error}</div>;
  }

  if (!isAccessGranted) {
    return (
      <TransporterAccess
        transporterSlug={transporterSlug}
        companyId={companyId}
        companyName={companyName}
        onLoginSuccess={handleLoginSuccess}
        showMessage={showMessage}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Icon
          path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
          className="w-12 h-12 text-blue-500 animate-spin"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.10),transparent_28%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.08),transparent_22%)] bg-slate-100">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-3 py-3 sm:px-5 sm:py-5 lg:px-8 lg:py-6">
        <div
          className={`relative overflow-hidden rounded-[28px] border bg-slate-900 transition-all duration-500 ${isHeaderCollapsed ? 'shadow-[0_18px_52px_rgba(2,6,23,0.22)]' : ''}`}
          style={headerShellStyle}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.18),transparent_32%),radial-gradient(circle_at_left,rgba(16,185,129,0.12),transparent_26%)]" />
          <div className="relative px-4 py-4 sm:px-6 sm:py-5 lg:px-7">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="driver-photo-shell relative h-14 w-14 overflow-hidden rounded-[20px] border border-white/20 text-sm font-bold text-slate-900 sm:h-16 sm:w-16 sm:text-base"
                  style={avatarShellStyle}
                >
                  <div className="pointer-events-none absolute inset-[3px] rounded-[inherit] border border-white/50" />
                  {transportPhotoURL ? (
                    <img
                      src={transportPhotoURL}
                      alt={transport?.currentDriverName || transport?.driverName || transport?.name || 'Chofer'}
                      className="driver-photo-img relative z-10 h-full w-full object-cover"
                    />
                  ) : (
                    <div className="relative z-10 flex h-full w-full items-center justify-center tracking-[0.18em]">
                      {transportAvatarLabel}
                    </div>
                  )}
                  {transportPhotoURL ? <span className="driver-photo-glow" /> : null}
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300/80">
                    <span className="inline-flex items-center gap-2 rounded-full border px-2.5 py-1" style={headerPillStyle}>
                      {resolvedCompanyName}
                    </span>
                  </div>

                  <h1 className="mt-2 truncate text-lg font-semibold text-white sm:text-2xl">
                    {titleText}
                  </h1>
                  <p className="mt-1 truncate text-sm text-slate-300/85">
                    {`Chofer: ${driverHeaderLabel}`}
                  </p>
                  <p className="mt-1 truncate text-xs text-slate-400/90">
                    {transportMetaLabel || 'Unidad operativa activa'}
                    {operationalAccessPath ? ` · ${operationalAccessPath}` : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="hidden items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300 sm:flex">
                  <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.14)]" />
                  Vista operativa
                </div>

                <button
                  type="button"
                  onClick={toggleHeaderCollapsed}
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-slate-200 transition hover:bg-white/10 ${showHeaderHint && isHeaderCollapsed ? 'animate-pulse shadow-[0_0_0_6px_rgba(255,255,255,0.06)]' : ''}`}
                  title={isHeaderCollapsed ? 'Expandir cabecera' : 'Plegar cabecera'}
                >
                  <Icon
                    path={isHeaderCollapsed ? 'M19.5 14.25L12 6.75l-7.5 7.5' : 'M4.5 9.75L12 17.25l7.5-7.5'}
                    className="h-5 w-5"
                  />
                </button>
              </div>
            </div>

            {isHeaderCollapsed ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <div className={`rounded-2xl border px-3 py-2 text-xs shadow-sm ${geoStatusMeta.tone}`}>
                  <div className="flex items-center gap-2 font-semibold">
                    <Icon
                      path={geoStatusMeta.icon}
                      className={`h-4 w-4 ${geoState.status === 'requesting' ? 'animate-spin' : ''}`}
                    />
                    <span>{geoStatusMeta.label}</span>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300">
                  Última señal: <span className="font-semibold text-white">{formatTimeAgo(geoState.lastSignalAt || route?.transporterLastLocation?.timestamp)}</span>
                  {route?.date && (
                    <span className="ml-2 text-[11px] text-slate-400">
                      {new Date(`${route.date}T00:00:00`).toLocaleDateString('es-AR')}
                    </span>
                  )}
                </div>

              </div>
            ) : (
              <>
                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                  <div className={`rounded-2xl border px-3.5 py-3 text-xs shadow-sm ${geoStatusMeta.tone}`}>
                    <div className="flex items-center gap-2 font-semibold">
                      <Icon
                        path={geoStatusMeta.icon}
                        className={`h-4 w-4 ${geoState.status === 'requesting' ? 'animate-spin' : ''}`}
                      />
                      <span>{geoStatusMeta.label}</span>
                    </div>
                    <p className="mt-1.5 text-[11px] opacity-80">
                      {route ? 'La ruta activa está enviando posición al monitor.' : 'La ubicación quedará lista apenas se asigne una hoja de ruta.'}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 px-3.5 py-3 text-xs text-slate-300 shadow-sm backdrop-blur-sm">
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Última señal
                    </span>
                    <span className="mt-1 block text-sm font-semibold text-white">
                      {formatTimeAgo(geoState.lastSignalAt || route?.transporterLastLocation?.timestamp)}
                    </span>
                    {route?.date && (
                      <span className="mt-1 block text-[11px] font-medium text-slate-300">
                        {new Date(`${route.date}T00:00:00`).toLocaleDateString('es-AR')}
                      </span>
                    )}
                    <p className="mt-1 text-[11px] text-slate-400">
                      Estado de comunicación entre el dispositivo y el monitor.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/5 px-3.5 py-3 text-xs text-slate-300 shadow-sm backdrop-blur-sm">
                    <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
                      Precisión GPS
                    </span>
                    <span className="mt-1 block text-sm font-semibold text-white">
                      {Number.isFinite(geoState.accuracy) && geoState.accuracy > 0
                        ? `±${Math.round(geoState.accuracy)} m`
                        : 'Esperando GPS'}
                    </span>
                    <p className="mt-1 text-[11px] text-slate-400">
                      Referencia estimada de exactitud de la señal actual.
                    </p>
                  </div>
                </div>

                {(geoState.error || !route || geoState.status === 'denied' || geoState.status === 'idle' || geoState.status === 'requesting' || geoState.status === 'error') && (
                  <div className="mt-4 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/92 px-4 py-3 text-sm text-slate-600 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <p className={`font-medium ${geoState.error ? 'text-rose-600' : 'text-slate-800'}`}>
                        {geoState.error || 'La ubicación ya quedó preparada. Cuando se asigne una hoja de ruta, el monitor verá tu posición en vivo.'}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Mantené activo el permiso de ubicación para una lectura más estable.
                      </p>
                    </div>

                    {(geoState.status === 'denied' || geoState.status === 'error' || geoState.status === 'idle' || geoState.status === 'requesting') && (
                      <button
                        onClick={handleRetryGeolocation}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-200 bg-cyan-50 px-3.5 py-2 text-xs font-semibold text-cyan-700 transition-colors hover:bg-cyan-100"
                      >
                        <Icon path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992" className="h-4 w-4" />
                        {geoState.status === 'active' ? 'Actualizar GPS' : 'Activar ubicación'}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex-1 pt-4 sm:pt-5">
          {route ? (
            <div className="rounded-[28px] border border-slate-200/70 bg-white/95 p-2 shadow-[0_20px_60px_rgba(15,23,42,0.18)] backdrop-blur-sm sm:p-3">
              <RouteDetail
                route={route}
                showMessage={showMessage}
                userRole="transportista"
                transporterLocation={effectiveTransporterLocation}
                transporterPath={effectiveTransporterPath}
                companyId={resolvedCompanyId}
                companyName={resolvedCompanyName}
                onRouteClosed={() => setRoute(null)}
              />
            </div>
          ) : (
            <div className="flex h-full min-h-[360px] flex-col items-center justify-center rounded-[28px] border border-white/10 bg-white/95 px-6 py-16 text-center shadow-[0_20px_60px_rgba(15,23,42,0.18)]">
              <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-slate-100 shadow-inner">
                <Icon
                  path="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128z"
                  className="h-10 w-10 text-slate-400"
                />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-slate-800">
                No tenés una ruta asignada para hoy.
              </h3>
              <p className="mt-2 max-w-xl text-sm text-slate-500">
                Tu acceso está listo. Apenas administración te asigne una hoja de ruta, vas a verla acá con los destinos y el seguimiento activo.
              </p>
            </div>
          )}
        </div>

        <div className="pt-4 pb-1 sm:pt-5">
          <div className="flex justify-end">
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-600 shadow-sm transition-colors hover:bg-rose-50"
            >
              <Icon path="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-7.5a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 006 21h7.5a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" className="h-4 w-4" />
              Cerrar sesión
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransporterLogisticsApp;
